import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader, AlertCircle, Check } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Consigner un relevé « Pourquoi je vois cette pub ? ».
 *
 * ## ⚠️ Conçue pour l'enchaînement, et c'est sa seule chance d'être employée
 *
 * Le protocole dure une à deux semaines, à raison de plusieurs relevés par jour. Si chaque
 * saisie demande de resélectionner le concurrent, la date et la surface, elle ne sera pas
 * faite — et un journal vide ne vaut rien. « Enregistrer et consigner le suivant » conserve
 * donc **la date et la surface**, et n'efface que les leviers, le verbatim et la créa.
 *
 * ## Deux choix qui paraissent anodins et ne le sont pas
 *
 * ⚠️ **Le rattachement à une créa est proposé, jamais exigé.** L'observateur a la publicité sous
 * les yeux, pas son `ad_archive_id`. Le champ propose les créas collectées ; `creaLibelle`
 * recueille en texte libre de quoi la reconnaître quand aucune ne correspond. Exiger le
 * rattachement ferait abandonner la saisie à la troisième publicité.
 *
 * ⚠️ **Le verbatim est obligatoire.** C'est la pièce du dossier : le classement en levier est
 * une interprétation, et Meta reformule ses panneaux sans préavis. Sans la phrase, un
 * reclassement futur obligerait à tout ressaisir.
 *
 * Les libellés, les aides et les indices de panneau viennent du serveur — voir
 * `leviersServis.js`. Rien n'est écrit en dur ici.
 */

/** ⚠️ `aujourdhui` en heure locale : `toISOString` daterait de la veille avant 1 h du matin. */
function maintenantLocal() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const ModaleObservation = ({
  concurrent, token, referentiel, observation = null, creas = [], onFermer, onEnregistre
}) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  const [observeLe, setObserveLe] = useState(maintenantLocal());
  const [source, setSource] = useState('PANNEAU_PUBLICITE');
  const [leviers, setLeviers] = useState([]);
  const [raisonBrute, setRaisonBrute] = useState('');
  const [surface, setSurface] = useState('');
  const [annonceId, setAnnonceId] = useState('');
  const [creaLibelle, setCreaLibelle] = useState('');
  const [note, setNote] = useState('');

  const [enregistrement, setEnregistrement] = useState(false);
  const [fautes, setFautes] = useState([]);
  const [confirmation, setConfirmation] = useState(null);

  const correction = observation !== null;

  useEffect(() => {
    if (!observation) return;
    setObserveLe(new Date(observation.observeLe).toISOString().slice(0, 16));
    setSource(observation.source);
    setLeviers(observation.leviers || []);
    setRaisonBrute(observation.raisonBrute || '');
    setSurface(observation.surface || '');
    setAnnonceId(observation.annonceId || '');
    setCreaLibelle(observation.creaLibelle || '');
    setNote(observation.note || '');
  }, [observation]);

  /*
   * ⚠️ Les préférences publicitaires ne désignent aucune créa : cet écran atteste une inclusion
   * dans un fichier, pas une publicité reçue. Le serveur le refuse ; l'écran retire le champ
   * plutôt que de laisser composer une saisie qui sera rejetée.
   */
  const creaPossible = source !== 'PREFERENCES_PUBLICITAIRES';
  useEffect(() => {
    if (!creaPossible) setAnnonceId('');
  }, [creaPossible]);

  const basculer = (cle) => setLeviers(l => (l.includes(cle) ? l.filter(x => x !== cle) : [...l, cle]));

  /** Les leviers groupés par famille : dix pastilles en vrac ne se parcourent pas. */
  const parFamille = useMemo(() => (referentiel?.familles || []).map(f => ({
    ...f,
    leviers: (referentiel?.leviers || []).filter(l => l.famille === f.id)
  })), [referentiel]);

  const corps = () => ({
    observeLe: new Date(observeLe).toISOString(),
    source,
    leviers,
    raisonBrute,
    surface: surface || null,
    annonceId: creaPossible && annonceId ? annonceId : null,
    creaLibelle: creaLibelle || null,
    note: note || null
  });

  const enregistrer = async (enchainer) => {
    // ⚠️ Deux barrières : les affordances d'écriture sont masquées pour un accès client, et
    // cette fonction sort tôt. Masquer seul ne suffit pas — une touche du clavier ou un chemin
    // de rendu oublié atteindraient encore la route, et un 403 rouge se lit comme une panne.
    if (enregistrement) return;

    setEnregistrement(true);
    setFautes([]);
    try {
      const url = correction
        ? `/api/competitors/${concurrent.id}/observations/${observation.id}`
        : `/api/competitors/${concurrent.id}/observations`;
      const methode = correction ? axios.put : axios.post;
      const { data } = await methode(url, corps(), { headers: { Authorization: `Bearer ${token}` } });

      onEnregistre?.(data.data);

      if (enchainer && !correction) {
        // ⚠️ La date et la surface restent : c'est tout l'intérêt. Le reste s'efface.
        setLeviers([]);
        setRaisonBrute('');
        setAnnonceId('');
        setCreaLibelle('');
        setNote('');
        setConfirmation('Relevé enregistré. Au suivant.');
        setTimeout(() => setConfirmation(null), 2500);
      } else {
        onFermer();
      }
    } catch (e) {
      // Le serveur rend TOUTES les fautes d'un coup : les afficher une par une ferait perdre
      // le relevé en cours.
      const reponse = e.response?.data;
      setFautes(reponse?.fautes || [reponse?.error || "L'enregistrement a échoué."]);
    } finally {
      setEnregistrement(false);
    }
  };

  const champ = { padding: '8px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
  const etiquette = { fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px', display: 'block' };

  return ReactDOM.createPortal(
    <div
      onClick={onFermer}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px',
          maxHeight: '90vh', overflowY: 'auto', padding: '24px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: '600' }}>
            {correction ? 'Corriger le relevé' : 'Consigner un relevé'}
          </h3>
          <button onClick={onFermer} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '20px' }}>
          {concurrent.name} — ce que le panneau affichait
        </p>

        {/* Instant et surface : conservés d'un relevé au suivant. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={etiquette}>Vu le</label>
            <input type="datetime-local" value={observeLe} onChange={e => setObserveLe(e.target.value)} style={champ} />
          </div>
          <div>
            <label style={etiquette}>Surface</label>
            <select value={surface} onChange={e => setSurface(e.target.value)} style={champ}>
              <option value="">Non précisée</option>
              {(referentiel?.surfaces || []).map(s => <option key={s.cle} value={s.cle}>{s.libelle}</option>)}
            </select>
          </div>
        </div>

        {/* La source : elle décide de ce que le relevé prouve. */}
        <div style={{ marginBottom: '16px' }}>
          <label style={etiquette}>D'où vient le relevé</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {(referentiel?.sources || []).map(s => (
              <label key={s.cle} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input type="radio" name="source" value={s.cle} checked={source === s.cle}
                  onChange={() => setSource(s.cle)} style={{ marginTop: '3px' }} />
                <span>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{s.libelle}</span>
                  <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', lineHeight: 1.4 }}>{s.aide}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Les leviers, groupés par famille, avec l'aide et les indices au survol. */}
        <div style={{ marginBottom: '16px' }}>
          <label style={etiquette}>Que disait le panneau ? (plusieurs choix possibles)</label>
          {parFamille.map(f => (
            <div key={f.id} style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>{f.nom}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {f.leviers.map(l => {
                  const actif = leviers.includes(l.cle);
                  const aide = [l.aide, l.indices?.length ? `Le panneau dit par exemple : « ${l.indices[0]} »` : null]
                    .filter(Boolean).join('\n\n');
                  return (
                    <button
                      key={l.cle}
                      type="button"
                      className="ksi-survolable"
                      onMouseEnter={survoler(aide)}
                      onMouseLeave={quitter}
                      onClick={() => basculer(l.cle)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '999px',
                        border: `1px solid ${actif ? '#3b82f6' : '#d1d5db'}`,
                        backgroundColor: actif ? '#eff6ff' : '#fff',
                        color: actif ? '#1d4ed8' : '#374151',
                        fontSize: '12px',
                        fontWeight: actif ? '600' : '400'
                      }}
                    >
                      {l.libelle}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* ⚠️ Le verbatim. Obligatoire, et l'étiquette dit pourquoi. */}
        <div style={{ marginBottom: '16px' }}>
          <label style={etiquette}>
            La phrase affichée, mot pour mot <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            value={raisonBrute}
            onChange={e => setRaisonBrute(e.target.value)}
            rows={3}
            placeholder="Vous voyez cette publicité car…"
            style={{ ...champ, fontFamily: 'inherit', resize: 'vertical' }}
          />
          <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', lineHeight: 1.4 }}>
            Le classement en levier est une interprétation ; la phrase, non. C'est elle qui
            permettra de reclasser si Meta reformule ses panneaux.
          </p>
        </div>

        {/* La créa : proposée, jamais exigée. */}
        {creaPossible && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={etiquette}>Créa collectée (facultatif)</label>
              <select value={annonceId} onChange={e => setAnnonceId(e.target.value)} style={champ}>
                <option value="">Aucune / introuvable</option>
                {creas.map(c => (
                  <option key={c.id} value={c.id}>
                    {(c.linkTitles || [])[0] || (c.bodies || [])[0]?.slice(0, 60) || c.externalAdId}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={etiquette}>Ou décrivez-la</label>
              <input value={creaLibelle} onChange={e => setCreaLibelle(e.target.value)}
                placeholder="vidéo peinture acrylique" style={champ} />
            </div>
          </div>
        )}
        {!creaPossible && (
          <p style={{
            fontSize: '12px', color: '#92400e', backgroundColor: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px',
            marginBottom: '16px', lineHeight: 1.45
          }}>
            Les préférences publicitaires attestent une inclusion dans un fichier, pas une
            publicité reçue : aucune créa ne s'y rattache. Décrivez le cas dans la note si besoin.
          </p>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={etiquette}>Note (facultatif)</label>
          <input value={note} onChange={e => setNote(e.target.value)} style={champ} />
        </div>

        {/* ⚠️ Toutes les fautes d'un coup, comme le serveur les rend. */}
        {fautes.length > 0 && (
          <div style={{
            display: 'flex', gap: '8px', padding: '12px', borderRadius: '8px',
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px'
          }}>
            <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }} />
            <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: 1.5 }}>
              {fautes.slice(0, 4).map((f, i) => <div key={i}>{f}</div>)}
              {fautes.length > 4 && <div>… et {fautes.length - 4} autre(s)</div>}
            </div>
          </div>
        )}

        {confirmation && (
          <div style={{
            display: 'flex', gap: '8px', alignItems: 'center', padding: '10px 12px',
            borderRadius: '8px', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0',
            marginBottom: '16px', fontSize: '12px', color: '#065f46'
          }}>
            <Check size={15} /> {confirmation}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onFermer} style={{
            padding: '9px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', fontSize: '13px', cursor: 'pointer'
          }}>
            Annuler
          </button>
          {!correction && (
            <button onClick={() => enregistrer(true)} disabled={enregistrement} style={{
              padding: '9px 16px', borderRadius: '8px', border: '1px solid #3b82f6',
              backgroundColor: '#fff', color: '#1d4ed8', fontSize: '13px', fontWeight: '500',
              cursor: enregistrement ? 'not-allowed' : 'pointer'
            }}>
              Enregistrer et consigner le suivant
            </button>
          )}
          <button onClick={() => enregistrer(false)} disabled={enregistrement} style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '9px 18px', borderRadius: '8px', border: 'none',
            backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '500',
            cursor: enregistrement ? 'not-allowed' : 'pointer'
          }}>
            {enregistrement && <Loader size={14} className="spin" />}
            {correction ? 'Enregistrer' : 'Enregistrer et fermer'}
          </button>
        </div>

        {bulle}
      </div>
    </div>,
    document.body
  );
};

export default ModaleObservation;
